import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Form, Button, Spinner, Accordion, Tabs, Tab } from 'react-bootstrap';
import { FaChartLine, FaUsers, FaEye, FaMousePointer, FaAd, FaChevronDown, FaChevronUp, FaCalendarDay, FaClock } from 'react-icons/fa';
import { dashboardApi, campaignsApi, analyticsApi } from '../../../services/sponsoredAdsApi';
import SetAlert from '../../../components/SetAlert';
import { FEED_PLACEMENT_LABELS } from '../feedAdPlacements';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area
} from 'recharts';

const SponsoredAdsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedAdSet, setSelectedAdSet] = useState('');
  const [selectedAd, setSelectedAd] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [alertMessage, setAlertMessage] = useState({ success: '', error: '' });
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [placementAnalytics, setPlacementAnalytics] = useState([]);
  const [placementLoading, setPlacementLoading] = useState(false);

  useEffect(() => {
    fetchDashboardSummary();
    fetchCampaigns();
  }, []);

  useEffect(() => {
    fetchDashboardSummary();
  }, [startDate, endDate, selectedCampaign, selectedAdSet, selectedAd]);

  useEffect(() => {
    setSelectedAdSet('');
    setSelectedAd('');
  }, [selectedCampaign]);

  useEffect(() => {
    setSelectedAd('');
  }, [selectedAdSet]);

  useEffect(() => {
    if (activeTab === 'placements' && selectedCampaign) {
      fetchPlacementAnalytics();
    }
  }, [activeTab, startDate, endDate, selectedCampaign]);

  const fetchPlacementAnalytics = async () => {
    if (!selectedCampaign) {
      setPlacementAnalytics([]);
      return;
    }
    setPlacementLoading(true);
    try {
      const response = await analyticsApi.getPlacementAnalytics({
        campaignId: selectedCampaign,
        startDate,
        endDate
      });
      if (response.statusCode === '10000') {
        setPlacementAnalytics(Array.isArray(response.data) ? response.data : []);
      } else {
        setPlacementAnalytics([]);
        setAlertMessage((prev) => ({
          ...prev,
          error: response.message || 'Failed to fetch placement analytics'
        }));
      }
    } catch (error) {
      console.error('Error fetching placement analytics:', error);
      setPlacementAnalytics([]);
    } finally {
      setPlacementLoading(false);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      const params = {
        startDate,
        endDate,
        campaignId: selectedCampaign,
        adSetId: selectedAdSet
      };

      console.log('Fetching dashboard summary with params:', params);
      const response = await dashboardApi.getHierarchicalSummary(params);
      console.log('Dashboard summary response:', response);

      if (response.statusCode === "10000") {
        setSummaryData(response.data);
        setAlertMessage(prev => ({ ...prev, success: response.message }));
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch dashboard summary' }));
      }
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching dashboard summary' }));
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await campaignsApi.getAll();
      if (response.statusCode === "10000") {
        setCampaigns(response.data || []);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch campaigns' }));
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching campaigns' }));
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString([], { 
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCTR = (clicks, impressions) => {
    const ctr = (clicks / impressions) * 100;
    return isNaN(ctr) ? '0.00' : ctr.toFixed(2);
  };

  const toggleCampaignExpansion = (campaignId) => {
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  const renderPerformanceCharts = (data, type) => {
    if (!data || !Array.isArray(data)) {
      console.log(`No valid data for ${type} performance chart`);
      return null;
    }

    // Filter out null dates/hours
    const validData = data.filter(item => item && (item.date || item.hour));

    // Sort data by date/hour
    const sortedData = [...validData].sort((a, b) => {
      if (!a || !b) return 0;
      
      if (type === 'daily') {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      }

      // For hourly data
      const hourA = a.hour ? new Date(a.hour).getTime() : 0;
      const hourB = b.hour ? new Date(b.hour).getTime() : 0;
      return hourA - hourB;
    });

    // Calculate min and max values for Y-axis
    const maxImpressions = Math.max(...sortedData.map(item => item?.impressions || item?.total_impressions || 0));
    const maxClicks = Math.max(...sortedData.map(item => item?.clicks || item?.total_clicks || 0));
    const maxUsers = Math.max(...sortedData.map(item => item?.unique_users || item?.total_unique_users || 0));
    const yAxisMax = Math.max(maxImpressions, maxClicks, maxUsers);

    return (
      <div className="chart-container" style={{ width: '100%', height: '400px', position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={sortedData}
            margin={{
              top: 10,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={type === 'daily' ? 'date' : 'hour'} 
              tickFormatter={(value) => {
                if (!value) return '';
                const date = new Date(value);
                if (type === 'daily') {
                  return date.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric'
                  });
                }
                return date.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                });
              }}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={type === 'daily' ? Math.ceil(sortedData.length / 20) : 0}
            />
            <YAxis 
              domain={[0, yAxisMax * 1.1]}
              tickFormatter={(value) => formatNumber(value)}
            />
            <Tooltip 
              formatter={(value) => [formatNumber(value || 0), '']}
              labelFormatter={(label) => {
                if (!label) return '';
                const date = new Date(label);
                if (type === 'daily') {
                  return date.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  });
                }
                return date.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey={type === 'daily' ? 'total_impressions' : 'total_impressions'}
              name="Impressions"
              stackId="1"
              stroke="#8884d8"
              fill="#8884d8"
            />
            <Area
              type="monotone"
              dataKey={type === 'daily' ? 'total_clicks' : 'total_clicks'}
              name="Clicks"
              stackId="2"
              stroke="#82ca9d"
              fill="#82ca9d"
            />
            <Area
              type="monotone"
              dataKey={type === 'daily' ? 'total_unique_users' : 'total_unique_users'}
              name="Unique Users"
              stackId="3"
              stroke="#ffc658"
              fill="#ffc658"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Helper to check if a date/hour is within the selected range
  const isWithinRange = (dateStr, start, end) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (start && d < new Date(start)) return false;
    if (end && d > new Date(end)) return false;
    return true;
  };

  // Filtered campaigns for selected campaign/ad set
  const getFilteredCampaigns = () => {
    let filtered = summaryData?.campaigns || [];
    if (selectedCampaign) {
      filtered = filtered.filter(c => c.campaign_id === parseInt(selectedCampaign));
    }
    return filtered;
  };

  // Filter ad sets for selected ad set
  const filterAdSets = (adSets) => {
    if (!selectedAdSet) return adSets;
    return adSets.filter(as => as.ad_set_id === parseInt(selectedAdSet));
  };

  // Filter daily/hourly performance by date range
  const filterPerformanceByDate = (arr, key = 'date') => {
    if (!Array.isArray(arr)) return [];
    return arr.filter(item => isWithinRange(item[key], startDate, endDate));
  };

  // --- DETAILED VIEW ---
  const renderDetailedPerformance = () => {
    const campaignsToShow = getFilteredCampaigns();
    if (!campaignsToShow.length) return <div className="text-center p-5">No data available</div>;

    // For each campaign, for each date, show an accordion with details inside
    return campaignsToShow.map(campaign => (
      <div key={campaign.campaign_id} className="mb-4">
        <h5>{campaign.campaign_name}</h5>
        <Accordion>
          {filterPerformanceByDate(campaign.daily_performance, 'date').map((day, i) => (
            <Accordion.Item eventKey={String(i)} key={i}>
              <Accordion.Header>
                <strong>{day.date ? new Date(day.date).toLocaleDateString() : 'No Date'}</strong>
                <span className="ms-3 text-muted">Impressions: {formatNumber(day.impressions || 0)} | Clicks: {formatNumber(day.clicks || 0)}</span>
              </Accordion.Header>
              <Accordion.Body>
                {filterAdSets(day.ad_sets || []).map(adSet => (
                  <div key={adSet.ad_set_id} className="mb-3">
                    <h6>{adSet.ad_set_name} <span className="text-muted">({adSet.brand_name})</span></h6>
                    <Table responsive size="sm">
                      <thead>
                        <tr>
                          <th>Impressions</th><th>Clicks</th><th>Unique Users</th><th>CTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{formatNumber(adSet.impressions || 0)}</td>
                          <td>{formatNumber(adSet.clicks || 0)}</td>
                          <td>{formatNumber(adSet.unique_users || 0)}</td>
                          <td>{formatCTR(adSet.clicks || 0, adSet.impressions || 0)}%</td>
                        </tr>
                      </tbody>
                    </Table>
                    {/* Ads */}
                    {Array.isArray(adSet.ads) && adSet.ads.length > 0 && (
                      <Table responsive size="sm" className="mt-2">
                        <thead>
                          <tr>
                            <th>Ad Name</th><th>Impressions</th><th>Clicks</th><th>Unique Users</th><th>CTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adSet.ads.map(ad => (
                            <tr key={ad.ad_id}>
                              <td>{ad.asset_name || 'Unnamed Ad'}</td>
                              <td>{formatNumber(ad.impressions || 0)}</td>
                              <td>{formatNumber(ad.clicks || 0)}</td>
                              <td>{formatNumber(ad.unique_users || 0)}</td>
                              <td>{formatCTR(ad.clicks || 0, ad.impressions || 0)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="sponsored-ads-dashboard">
      <SetAlert alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      
      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Total Campaigns</h6>
                  <h3>{formatNumber(summaryData?.total_campaigns || 0)}</h3>
                </div>
                <FaChartLine className="text-primary" size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Total Ad Sets</h6>
                  <h3>{formatNumber(summaryData?.total_ad_sets || 0)}</h3>
                </div>
                <FaAd className="text-success" size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Total Impressions</h6>
                  <h3>{formatNumber(summaryData?.total_impressions || 0)}</h3>
                </div>
                <FaEye className="text-warning" size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Total Clicks</h6>
                  <h3>{formatNumber(summaryData?.total_clicks || 0)}</h3>
                </div>
                <FaMousePointer className="text-danger" size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Campaign</Form.Label>
                <Form.Select
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                >
                  <option value="">All Campaigns</option>
                  {Array.isArray(campaigns) && campaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                      {campaign.campaign_name}
                      </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Ad Set</Form.Label>
                <Form.Select
                  value={selectedAdSet}
                  onChange={(e) => setSelectedAdSet(e.target.value)}
                  disabled={!selectedCampaign}
                >
                  <option value="">All Ad Sets</option>
                  {selectedCampaign && Array.isArray(summaryData?.campaigns) && 
                    summaryData.campaigns
                      .find(c => c.campaign_id === parseInt(selectedCampaign))
                      ?.ad_sets?.map((adSet) => (
                        <option key={adSet.ad_set_id} value={adSet.ad_set_id}>
                          {adSet.ad_set_name}
                        </option>
                      ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Overall Performance */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Overall Performance</h5>
            <div className="text-muted">
              {startDate && endDate ? (
                <span>
                  {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
                </span>
              ) : (
                <span>All Time</span>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab eventKey="overview" title="Overview">
              <Row>
                <Col md={12}>
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">
                        <FaCalendarDay className="me-2" />
                        Daily Performance
                      </h6>
                    </Card.Header>
                    <Card.Body style={{ minHeight: '450px' }}>
                      {loading ? (
                        <div className="text-center p-5">
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </Spinner>
                        </div>
                      ) : !Array.isArray(summaryData?.daily_performance) ? (
                        <div className="text-center p-5">
                          <p>No daily performance data available</p>
                          <small className="text-muted">Please check the date range and filters</small>
                        </div>
                      ) : (
                        renderPerformanceCharts(summaryData.daily_performance, 'daily')
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={12}>
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">
                        <FaClock className="me-2" />
                        Hourly Performance
                      </h6>
                    </Card.Header>
                    <Card.Body style={{ minHeight: '450px' }}>
                      {loading ? (
                        <div className="text-center p-5">
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </Spinner>
                        </div>
                      ) : !Array.isArray(summaryData?.hourly_performance) ? (
                        <div className="text-center p-5">
                          <p>No hourly performance data available</p>
                          <small className="text-muted">Please check the date range and filters</small>
                        </div>
                      ) : (
                        renderPerformanceCharts(summaryData.hourly_performance, 'hourly')
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Performance Summary</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <div className="text-center">
                            <h6 className="text-muted mb-2">Total CTR</h6>
                            <h3>{formatCTR(summaryData?.total_clicks || 0, summaryData?.total_impressions || 0)}%</h3>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h6 className="text-muted mb-2">Total Unique Users</h6>
                            <h3>{formatNumber(summaryData?.total_unique_users || 0)}</h3>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h6 className="text-muted mb-2">Total Ads</h6>
                            <h3>{formatNumber(summaryData?.total_ads || 0)}</h3>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h6 className="text-muted mb-2">Total Campaigns</h6>
                            <h3>{formatNumber(summaryData?.total_campaigns || 0)}</h3>
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-4">
                        <Col md={12}>
                          <Table responsive hover>
                            <thead>
                              <tr>
                                <th>Metric</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Total Impressions</td>
                                <td>{formatNumber(summaryData?.total_impressions || 0)}</td>
                              </tr>
                              <tr>
                                <td>Total Clicks</td>
                                <td>{formatNumber(summaryData?.total_clicks || 0)}</td>
                              </tr>
                              <tr>
                                <td>Total Unique Users</td>
                                <td>{formatNumber(summaryData?.total_unique_users || 0)}</td>
                              </tr>
                              <tr>
                                <td>Total CTR</td>
                                <td>{formatCTR(summaryData?.total_clicks || 0, summaryData?.total_impressions || 0)}%</td>
                              </tr>
                              <tr>
                                <td>Total Campaigns</td>
                                <td>{formatNumber(summaryData?.total_campaigns || 0)}</td>
                              </tr>
                              <tr>
                                <td>Total Ad Sets</td>
                                <td>{formatNumber(summaryData?.total_ad_sets || 0)}</td>
                              </tr>
                              <tr>
                                <td>Total Ads</td>
                                <td>{formatNumber(summaryData?.total_ads || 0)}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="placements" title="By Placement">
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Feed Ad Performance by Placement</h6>
                </Card.Header>
                <Card.Body>
                  {!selectedCampaign ? (
                    <div className="text-center p-4 text-muted">
                      Select a campaign in the filters above to view placement breakdown.
                    </div>
                  ) : placementLoading ? (
                    <div className="text-center p-5">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                  ) : placementAnalytics.length === 0 ? (
                    <div className="text-center p-4 text-muted">
                      No placement analytics for this campaign and date range.
                    </div>
                  ) : (
                    <>
                      <Row className="mb-4">
                        <Col md={12}>
                          <div style={{ width: '100%', height: '320px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={placementAnalytics} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="placement"
                                  tickFormatter={(v) => FEED_PLACEMENT_LABELS[v] || v}
                                />
                                <YAxis tickFormatter={(v) => formatNumber(v)} />
                                <Tooltip
                                  formatter={(value, name) => [formatNumber(value || 0), name]}
                                  labelFormatter={(label) => FEED_PLACEMENT_LABELS[label] || label}
                                />
                                <Legend />
                                <Bar dataKey="impressions" fill="#8884d8" name="Impressions" />
                                <Bar dataKey="clicks" fill="#82ca9d" name="Clicks" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </Col>
                      </Row>
                      <Table responsive hover>
                        <thead>
                          <tr>
                            <th>Placement</th>
                            <th>Impressions</th>
                            <th>Clicks</th>
                            <th>CTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {placementAnalytics.map((row) => (
                            <tr key={row.placement}>
                              <td>{FEED_PLACEMENT_LABELS[row.placement] || row.placement}</td>
                              <td>{formatNumber(row.impressions || 0)}</td>
                              <td>{formatNumber(row.clicks || 0)}</td>
                              <td>{(row.ctr ?? formatCTR(row.clicks || 0, row.impressions || 0))}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab>
            <Tab eventKey="detailed" title="Detailed View">
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Detailed Performance Breakdown</h6>
                    <div className="text-muted">
                      {startDate && endDate ? (
                        <span>
                          {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
                        </span>
                      ) : (
                        <span>All Time</span>
                      )}
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="text-center p-5">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                  ) : !Array.isArray(summaryData?.hourly_performance) ? (
                    <div className="text-center p-5">
                      <p>No detailed performance data available</p>
                    </div>
                  ) : (
                    renderDetailedPerformance()
                  )}
                </Card.Body>
              </Card>
            </Tab>
            <Tab eventKey="campaigns" title="Campaigns">
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : !Array.isArray(summaryData?.campaigns) || summaryData.campaigns.length === 0 ? (
                <div className="text-center p-5">
                  <p>No campaign data available</p>
                  <small className="text-muted">Please check the date range and filters</small>
                </div>
              ) : (
                <div>
                  <Tabs
                    defaultActiveKey={summaryData.campaigns[0]?.campaign_id.toString()}
                    className="mb-3"
                  >
                    {summaryData.campaigns.map((campaign) => {
                      // Find matching campaign from campaigns list for additional details
                      const campaignDetails = campaigns.find(c => c.id === campaign.campaign_id);
                      
                      return (
                        <Tab
                          key={campaign.campaign_id}
                          eventKey={campaign.campaign_id.toString()}
                          title={
                            <div>
                              <div>{campaign.campaign_name}</div>
                              <small className="text-muted">
                                Impressions: {formatNumber(campaign.impressions || 0)} | 
                                Clicks: {formatNumber(campaign.clicks || 0)} | 
                                CTR: {formatCTR(campaign.clicks || 0, campaign.impressions || 0)}%
                              </small>
                            </div>
                          }
                        >
                          <div>
                            {/* Campaign Details */}
                            <Card className="mb-4">
                              <Card.Header>
                                <h6 className="mb-0">Campaign Details</h6>
                              </Card.Header>
                              <Card.Body>
                                <Row>
                                  <Col md={6}>
                                    <Table responsive>
                                      <tbody>
                                        <tr>
                                          <td><strong>Campaign Name</strong></td>
                                          <td>{campaign.campaign_name}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>Status</strong></td>
                                          <td>{campaignDetails?.status || 'N/A'}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>Frequency</strong></td>
                                          <td>{campaignDetails?.frequency_percentage || 0}%</td>
                                        </tr>
                                        <tr>
                                          <td><strong>Start Time</strong></td>
                                          <td>{campaignDetails?.start_time ? new Date(campaignDetails.start_time).toLocaleString() : 'N/A'}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>End Time</strong></td>
                                          <td>{campaignDetails?.end_time ? new Date(campaignDetails.end_time).toLocaleString() : 'N/A'}</td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  </Col>
                                  <Col md={6}>
                                    <Table responsive>
                                      <tbody>
                                        <tr>
                                          <td><strong>Total Impressions</strong></td>
                                          <td>{formatNumber(campaign.impressions || 0)}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>Total Clicks</strong></td>
                                          <td>{formatNumber(campaign.clicks || 0)}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>Total Unique Users</strong></td>
                                          <td>{formatNumber(campaign.unique_users || 0)}</td>
                                        </tr>
                                        <tr>
                                          <td><strong>CTR</strong></td>
                                          <td>{formatCTR(campaign.clicks || 0, campaign.impressions || 0)}%</td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>

                            {/* Campaign Performance Summary */}
                            <Card className="mb-4">
                              <Card.Header>
                                <h6 className="mb-0">Campaign Performance Summary</h6>
                              </Card.Header>
                              <Card.Body>
                                <Row>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h6 className="text-muted mb-2">Total Impressions</h6>
                                      <h3>{formatNumber(campaign.impressions || 0)}</h3>
                                    </div>
                                  </Col>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h6 className="text-muted mb-2">Total Clicks</h6>
                                      <h3>{formatNumber(campaign.clicks || 0)}</h3>
                                    </div>
                                  </Col>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h6 className="text-muted mb-2">Total Unique Users</h6>
                                      <h3>{formatNumber(campaign.unique_users || 0)}</h3>
                                    </div>
                                  </Col>
                                  <Col md={3}>
                                    <div className="text-center">
                                      <h6 className="text-muted mb-2">CTR</h6>
                                      <h3>{formatCTR(campaign.clicks || 0, campaign.impressions || 0)}%</h3>
                                    </div>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>

                            {/* Ad Sets Performance */}
                            {campaign.daily_performance && campaign.daily_performance[0]?.ad_sets && (
                              <Card className="mb-4">
                                <Card.Header>
                                  <h6 className="mb-0">Ad Sets Performance</h6>
                                </Card.Header>
                                <Card.Body>
                                  {/* Ad Sets Performance Summary */}
                                  <Row className="mb-4">
                                    <Col md={12}>
                                      <Table responsive hover>
                                        <thead>
                                          <tr>
                                            <th>Ad Set</th>
                                            <th>Brand</th>
                                            <th>Total Impressions</th>
                                            <th>Total Clicks</th>
                                            <th>Unique Users</th>
                                            <th>CTR</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {campaign.daily_performance[0].ad_sets.map((adSet) => (
                                            <tr key={adSet.ad_set_id}>
                                              <td>{adSet.ad_set_name || 'N/A'}</td>
                                              <td>{adSet.brand_name || 'N/A'}</td>
                                              <td>{formatNumber(adSet.impressions || 0)}</td>
                                              <td>{formatNumber(adSet.clicks || 0)}</td>
                                              <td>{formatNumber(adSet.unique_users || 0)}</td>
                                              <td>{formatCTR(adSet.clicks || 0, adSet.impressions || 0)}%</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </Col>
                                  </Row>

                                  {/* Ad Sets Performance Chart */}
                                  <Row className="mb-4">
                                    <Col md={12}>
                                      <Card>
                                        <Card.Header>
                                          <h6 className="mb-0">Ad Sets Performance Over Time</h6>
                                        </Card.Header>
                                        <Card.Body style={{ height: '400px' }}>
                                          {renderPerformanceCharts(
                                            campaign.daily_performance
                                              .filter(item => item.date !== null)
                                              .map(item => {
                                                const chartData = {
                                                  date: item.date,
                                                  total_impressions: item.impressions || 0,
                                                  total_clicks: item.clicks || 0,
                                                  total_unique_users: item.unique_users || 0
                                                };

                                                // Add ad set specific data
                                                if (item.ad_sets && Array.isArray(item.ad_sets)) {
                                                  item.ad_sets.forEach(adSet => {
                                                    if (adSet && adSet.ad_set_name) {
                                                      chartData[`${adSet.ad_set_name}_impressions`] = adSet.impressions || 0;
                                                      chartData[`${adSet.ad_set_name}_clicks`] = adSet.clicks || 0;
                                                    }
                                                  });
                                                }

                                                return chartData;
                                              }),
                                            'daily'
                                          )}
                                        </Card.Body>
                                      </Card>
                                    </Col>
                                  </Row>

                                  {/* Individual Ad Sets */}
                                  <Accordion>
                                    {campaign.daily_performance[0].ad_sets.map((adSet) => (
                                      <Accordion.Item key={adSet.ad_set_id} eventKey={adSet.ad_set_id?.toString() || 'default'}>
                                        <Accordion.Header>
                                          <div className="d-flex justify-content-between align-items-center w-100">
                                            <div>
                                              <h6 className="mb-0">{adSet.ad_set_name || 'Unnamed Ad Set'}</h6>
                                              <small className="text-muted">
                                                Brand: {adSet.brand_name || 'N/A'} | 
                                                Impressions: {formatNumber(adSet.impressions || 0)} | 
                                                Clicks: {formatNumber(adSet.clicks || 0)} | 
                                                CTR: {formatCTR(adSet.clicks || 0, adSet.impressions || 0)}%
                                              </small>
                                            </div>
                                          </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                          <div className="ad-set-details">
                                            <Table responsive>
                                              <tbody>
                                                <tr>
                                                  <td><strong>Brand Name</strong></td>
                                                  <td>{adSet.brand_name || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                  <td><strong>Total Impressions</strong></td>
                                                  <td>{formatNumber(adSet.impressions || 0)}</td>
                                                </tr>
                                                <tr>
                                                  <td><strong>Total Clicks</strong></td>
                                                  <td>{formatNumber(adSet.clicks || 0)}</td>
                                                </tr>
                                                <tr>
                                                  <td><strong>Unique Users</strong></td>
                                                  <td>{formatNumber(adSet.unique_users || 0)}</td>
                                                </tr>
                                                <tr>
                                                  <td><strong>CTR</strong></td>
                                                  <td>{formatCTR(adSet.clicks || 0, adSet.impressions || 0)}%</td>
                                                </tr>
                                              </tbody>
                                            </Table>

                                            {/* Ad Set Performance Charts */}
                                            {Array.isArray(campaign.daily_performance) && campaign.daily_performance.length > 0 && (
                                              <div className="mt-4">
                                                <h6 className="mb-3">Daily Performance</h6>
                                                <div style={{ height: '300px' }}>
                                                  {renderPerformanceCharts(
                                                    campaign.daily_performance
                                                      .filter(item => item.date !== null)
                                                      .map(item => {
                                                        const adSetData = item.ad_sets?.find(as => as.ad_set_id === adSet.ad_set_id) || {};
                                                        return {
                                                          date: item.date,
                                                          total_impressions: adSetData.impressions || 0,
                                                          total_clicks: adSetData.clicks || 0,
                                                          total_unique_users: adSetData.unique_users || 0
                                                        };
                                                      }),
                                                    'daily'
                                                  )}
                                                </div>
                                              </div>
                                            )}

                                            {/* Ads Performance */}
                                            {Array.isArray(adSet.ads) && adSet.ads.length > 0 && (
                                              <div className="mt-4">
                                                <h6 className="mb-3">Ads Performance</h6>
                                                <Table responsive hover>
                                                  <thead>
                                                    <tr>
                                                      <th>Ad Name</th>
                                                      <th>Impressions</th>
                                                      <th>Clicks</th>
                                                      <th>Unique Users</th>
                                                      <th>CTR</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {adSet.ads.map((ad) => (
                                                      <tr key={ad.ad_id}>
                                                        <td>{ad.asset_name || 'Unnamed Ad'}</td>
                                                        <td>{formatNumber(ad.impressions || 0)}</td>
                                                        <td>{formatNumber(ad.clicks || 0)}</td>
                                                        <td>{formatNumber(ad.unique_users || 0)}</td>
                                                        <td>{formatCTR(ad.clicks || 0, ad.impressions || 0)}%</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </Table>
                                              </div>
                                            )}
                                          </div>
                                        </Accordion.Body>
                                      </Accordion.Item>
                                    ))}
                                  </Accordion>
                                </Card.Body>
                              </Card>
                            )}

                            {/* Detailed Performance Breakdown */}
                            {Array.isArray(campaign.hourly_performance) && campaign.hourly_performance.length > 0 && (
                              <Card className="mb-4">
                                <Card.Header>
                                  <h6 className="mb-0">Detailed Performance Breakdown</h6>
                                </Card.Header>
                                <Card.Body>
                                  {renderDetailedPerformance()}
                                </Card.Body>
                              </Card>
                            )}
                          </div>
                        </Tab>
                      );
                    })}
                  </Tabs>
                </div>
              )}
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SponsoredAdsDashboard; 